/**
 * The "Live events" group in Settings > Notifications (2026-10-30): the
 * master switch for the automatic countdowns, then the same controls the
 * intro sheet collects (card or notification, how long before, which event
 * types) and "Send me an example". Every change saves to the account at
 * once and re-arms this phone.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { LiveEventPrefsControls } from "@/components/LiveEventPrefsControls";
import { openLiveEventsSetup } from "@/components/LiveEventsIntroSheet";
import { useToast } from "@/hooks/use-toast";
import { previewLiveActivity, startLiveActivityListeners } from "@/lib/liveActivities";
import { armSubscribedEventsFromFeed } from "@/lib/liveEventArming";
import { saveLiveEventPrefs, suggestedPrefs, useLiveEventPrefs, type LiveEventPrefs } from "@/lib/liveEventPrefs";
import { fireLocalNotification, requestNotificationPermission } from "@/lib/notifications";
import { getPlatform } from "@/lib/platform";

export function LiveEventsSettingsGroup({ disabled = false }: { disabled?: boolean }): JSX.Element {
  const prefs = useLiveEventPrefs();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const platform = getPlatform();
  const native = platform === "ios" || platform === "android";
  const [saving, setSaving] = useState(false);

  const save = async (next: LiveEventPrefs) => {
    setSaving(true);
    try {
      if (next.enabled && native && (platform === "android" || next.delivery === "push")) {
        const perm = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
        if (perm !== "granted") {
          toast({
            title: "Notifications are off for Nexus Dex.",
            description: "Turn them on for Nexus Dex in your phone's Settings and the countdowns will arrive.",
          });
        }
      }
      if (next.enabled && platform === "ios" && next.delivery === "live") void startLiveActivityListeners();
      await saveLiveEventPrefs(next);
      if (next.enabled) void armSubscribedEventsFromFeed(queryClient, { prefs: next });
    } finally {
      setSaving(false);
    }
  };

  const example = async () => {
    const p = prefs ?? suggestedPrefs();
    if (!native) {
      toast({ title: "The countdown is a phone thing.", description: "Open Nexus Dex on your phone to see it live." });
      return;
    }
    if (p.delivery === "live") {
      const r = await previewLiveActivity(Date.now(), p.leadMinutes * 60_000);
      toast({ title: r.ok ? "Look at your Lock Screen." : "No countdown on this phone.", description: r.detail });
      return;
    }
    const perm = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
    if (perm !== "granted") {
      toast({ title: "Notifications are off for Nexus Dex.", description: "Turn them on for Nexus Dex in your phone's Settings." });
      return;
    }
    const r = await fireLocalNotification({
      id: 990_001,
      title: p.leadMinutes === 0 ? "Pikachu Spotlight Hour is live" : `Pikachu Spotlight Hour starts in ${p.leadMinutes === 60 ? "1 hour" : `${p.leadMinutes} minutes`}`,
      body: "2× Catch Stardust. Open Pokémon GO.",
    });
    toast({ title: r.ok ? "Sent." : "Could not send the example.", description: r.ok ? "That is the notification you would get." : r.detail });
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="settings-live-events">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Live events</p>
          <p className="text-[11px] text-muted-foreground leading-snug">
            {platform === "ios"
              ? "An automatic countdown on your Lock Screen and in the Dynamic Island for every event type you tick, then the event while it is on."
              : platform === "android"
                ? "An automatic countdown in your notifications for every event type you tick, then the event while it is on."
                : "An automatic countdown on your phone for every event type you tick. Set here, it arrives on the Nexus Dex app."}
          </p>
        </div>
        <Switch
          checked={prefs?.enabled === true}
          onCheckedChange={(v) => void save({ ...(prefs ?? suggestedPrefs()), enabled: v })}
          disabled={disabled || saving}
          data-testid="toggle-live-events"
        />
      </div>

      {prefs == null && (
        <button
          type="button"
          onClick={openLiveEventsSetup}
          className="mt-2 text-[11px] font-bold"
          style={{ color: "var(--accent-fill)" }}
          data-testid="settings-live-events-setup"
        >
          Set up live events
        </button>
      )}

      {prefs?.enabled && (
        <div className="mt-3">
          <LiveEventPrefsControls value={prefs} onChange={(next) => void save(next)} showDelivery={native} testId="settings-live-events" />
          <button
            type="button"
            onClick={() => void example()}
            className="mt-2 text-[10px] font-bold"
            style={{ color: "var(--accent-fill)" }}
            data-testid="settings-live-events-preview"
          >
            Send me an example
          </button>
          <p className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
            Only events with a moment to count down to get a card: an hour, an afternoon, or a weekend like GO Fest. A
            week-long raid rotation, a Timed Research or a Season never does, whatever is ticked. Those events show
            "Countdown set" on the Calendar instead of Remind me. One-place events (a GO Tour city) are only counted down
            when you mark yourself as going. Remind me still works on any other event, as a notification.
          </p>
        </div>
      )}
    </div>
  );
}
